// gioco/maglia.ts — partita a 4 (v013): il video "a maglia". Ogni telefono ha una videochiamata (video.ts) con
// ciascuno degli altri giocatori collegati: manda il suo video a tutti e riceve quello di tutti. Il server inoltra solo
// i messaggi per mettersi d'accordo (con il posto del destinatario); il video passa diretto o dal TURN.
//
// Ogni coppia si rifà quando cambia il contatore dei collegamenti di uno dei due (lo dice il server, così la
// rifanno insieme tutti e due i telefoni). Le videochiamate nuove partono un po' distanziate, per non mandare troppi
// messaggi insieme al server.
import { Videochiamata, type StatoVideo } from "./video.ts";
import type { DatiSegnale } from "../../../condiviso/protocollo.ts";
import { numeroPosto, type Posto } from "../../../condiviso/gruppo.ts";
import type { ServerIce } from "../servizi/sfide.ts";
import { eNumero, leggiImpostazione } from "../servizi/impostazioni.ts";
import { IMP_APP } from "../costanti.ts";

const ATTESA_SEGNALI_MS = 10_000;
/** «mia-tua» vista dall'altro telefono è «tua-mia». */
const rovescia = (chiave: string): string => chiave.split("-").reverse().join("-");
const MASSIMO_IN_ATTESA = 60;
/** Quante volte richiedere i server video prima di provare senza. */
const TENTATIVI_ICE = 5;

export interface OpzioniMaglia {
  io: Posto;
  ice: () => Promise<ServerIce[]>;
  locale: () => MediaStream;
  /** `sessione`: la chiave della coppia vista da chi manda («mia-tua»). */
  invia: (con: Posto, d: DatiSegnale, sessione: string) => void;
  suRemoto: (p: Posto, s: MediaStream | null) => void;
  suStato: (p: Posto, s: StatoVideo) => void;
  soloTurn?: boolean;
  /** Pausa tra una videochiamata nuova e la successiva. */
  distanzaMs?: number;
}

interface Collegamento {
  chiave: string;
  chiamata: Videochiamata | null;
  coda: DatiSegnale[];
  timer: ReturnType<typeof setTimeout> | null;
}

export class Maglia {
  private o: OpzioniMaglia;
  private c = new Map<Posto, Collegamento>();
  private chiusa = false;
  private prossimoAvvio = 0;
  /** Segnali arrivati da chi non è ancora tra i voluti (la sua vista è arrivata prima della mia): si tengono un po'. */
  private inAttesa = new Map<Posto, { d: DatiSegnale; il: number; sessione: string | undefined }[]>();

  constructor(o: OpzioniMaglia) {
    this.o = o;
  }

  /** Con chi ci si deve vedere adesso: posto → chiave della coppia (cambia = si rifà). */
  aggiorna(voluti: Partial<Record<Posto, string>>): void {
    if (this.chiusa) return;
    for (const [p, col] of this.c) {
      if (voluti[p] !== col.chiave) this.togli(p);
    }
    for (const [p, chiave] of Object.entries(voluti) as [Posto, string][]) {
      if (p === this.o.io || this.c.has(p)) continue;
      const ora0 = Date.now();
      const attesa = rovescia(chiave);
      const tutti = this.inAttesa.get(p) ?? [];
      const prima = tutti.filter((x) => ora0 - x.il < ATTESA_SEGNALI_MS && (x.sessione === undefined || x.sessione === attesa)).map((x) => x.d);
      this.inAttesa.set(p, tutti.filter((x) => ora0 - x.il < ATTESA_SEGNALI_MS && x.sessione !== undefined && x.sessione !== attesa));
      const col: Collegamento = { chiave, chiamata: null, coda: prima, timer: null };
      this.c.set(p, col);
      const ora = Date.now();
      const quando = Math.max(ora, this.prossimoAvvio);
      this.prossimoAvvio = quando + (this.o.distanzaMs ?? 350);
      col.timer = setTimeout(() => void this.avvia(p, col), quando - ora);
    }
  }

  private async avvia(p: Posto, col: Collegamento, tentativo = 0): Promise<void> {
    col.timer = null;
    let server: ServerIce[];
    try {
      server = await this.o.ice();
    } catch {
      if (this.chiusa || this.c.get(p) !== col) return;
      if (tentativo < TENTATIVI_ICE) {
        // Server video non avuti (rete, limite): si riprova con attesa crescente.
        col.timer = setTimeout(() => void this.avvia(p, col, tentativo + 1), Math.min(15_000, 1_000 * 2 ** tentativo));
        return;
      }
      server = []; // ultima possibilità: solo i collegamenti diretti
    }
    if (this.chiusa || this.c.get(p) !== col) return;
    col.chiamata = new Videochiamata({
      iceServers: server,
      // Nella coppia cede chi ha il posto più alto (offerte incrociate).
      educato: numeroPosto(this.o.io) > numeroPosto(p),
      locale: this.o.locale(),
      soloTurn: this.o.soloTurn,
      // v038: nella partita a 4 ogni telefono manda 3 video: più leggeri (meno dati e meno batteria)
      limiteVideo: { kbps: leggiImpostazione(IMP_APP.gruppiVideoKbps, 450, eNumero), altezza: leggiImpostazione(IMP_APP.gruppiVideoAltezza, 360, eNumero) },
      inviaSegnale: (d) => this.o.invia(p, d, col.chiave),
      suRemoto: (s) => this.o.suRemoto(p, s),
      suStato: (s) => this.o.suStato(p, s),
    });
    for (const d of col.coda.splice(0)) void col.chiamata.ricevi(d);
  }

  ricevi(da: Posto, d: DatiSegnale, sessione?: string): void {
    const col = this.c.get(da);
    // Un segnale di un'altra coppia di collegamenti (vecchia, o nuova che qui non è ancora arrivata) non va alla
    // videochiamata di adesso: si tiene un po' per quando arriverà la vista che fa nascere la sua coppia.
    if (!col || (sessione !== undefined && sessione !== rovescia(col.chiave))) {
      const l = this.inAttesa.get(da) ?? [];
      l.push({ d, il: Date.now(), sessione });
      this.inAttesa.set(da, l.slice(-MASSIMO_IN_ATTESA));
      return;
    }
    if (col.chiamata) void col.chiamata.ricevi(d);
    else col.coda.push(d);
  }

  /** Il video inviato cambia (filtro acceso o spento): si sostituisce la traccia, senza rinegoziare. */
  sostituisciVideo(traccia: MediaStreamTrack): void {
    for (const col of this.c.values()) void col.chiamata?.sostituisciVideo(traccia).catch(() => undefined);
  }

  /** v026: la voce inviata cambia (voce buffa): si sostituisce la traccia audio a tutti. */
  sostituisciAudio(traccia: MediaStreamTrack): void {
    for (const col of this.c.values()) void col.chiamata?.sostituisciAudio(traccia).catch(() => undefined);
  }

  private togli(p: Posto): void {
    const col = this.c.get(p);
    if (!col) return;
    if (col.timer) clearTimeout(col.timer);
    col.chiamata?.chiudi();
    this.c.delete(p);
    this.o.suRemoto(p, null);
  }

  chiudi(): void {
    this.chiusa = true;
    for (const p of [...this.c.keys()]) this.togli(p);
  }
}
